import { useEffect, useState } from 'react';

const TICK_MS = 24;
// A long reply would crawl at one character per tick, so the step scales with
// length to keep the whole reveal inside roughly this budget.
const TARGET_REVEAL_MS = 3500;

export const charsPerTick = (length: number) => Math.max(
  1,
  Math.ceil(length / (TARGET_REVEAL_MS / TICK_MS)),
);

// Cutting mid-word would flash half-written markdown markers, so the visible
// slice stops at the last whitespace until the text is fully revealed.
export const toWordBoundary = (text: string, length: number): string => {
  if (length >= text.length) return text;

  const revealed = text.slice(0, length);
  const boundary = Math.max(revealed.lastIndexOf(' '), revealed.lastIndexOf('\n'));
  return boundary > 0 ? revealed.slice(0, boundary) : '';
};

// Reveals text progressively, as if it were being written. Returns the whole
// text at once when disabled, so replayed history appears complete.
const useTypewriter = (text: string, enabled: boolean): string => {
  const [revealedLength, setRevealedLength] = useState(0);

  useEffect(() => {
    setRevealedLength(enabled ? 0 : text.length);
  }, [text, enabled]);

  useEffect(() => {
    if (!enabled || revealedLength >= text.length) return undefined;

    const step = charsPerTick(text.length);
    const timer = window.setTimeout(() => {
      setRevealedLength((current) => Math.min(current + step, text.length));
    }, TICK_MS);

    return () => window.clearTimeout(timer);
  }, [enabled, revealedLength, text]);

  return enabled ? toWordBoundary(text, revealedLength) : text;
};

export default useTypewriter;
